import { useTranslation } from 'react-i18next';
import { FallbackProps } from 'react-error-boundary';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { getErrorPresentation } from '@/lib/errors';

interface ErrorFallbackProps extends FallbackProps {
  showHomeLink?: boolean;
}

export function ErrorFallback({
  error,
  showHomeLink = true,
}: ErrorFallbackProps) {
  const { t } = useTranslation();

  const { isServerError, requestId } = getErrorPresentation(error);

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center space-y-4">
      <div className="space-y-1">
        {isServerError ? (
          <h1 className="text-7xl font-bold tabular-nums vess-iridescent-text">500</h1>
        ) : (
          <AlertTriangle className="h-14 w-14 mx-auto text-muted-foreground" />
        )}
        <p className="text-xl font-semibold tracking-tight">
          {isServerError ? t('errors.serverError') : t('errors.applicationError')}
        </p>
        {requestId && (
          <p className="text-xs text-muted-foreground font-mono">
            {t('errors.supportReference', { requestId: requestId.slice(0, 8) })}
          </p>
        )}
      </div>

      <p className="text-muted-foreground text-sm">
        {isServerError
          ? t('errors.serverErrorMessage')
          : t('errors.applicationErrorMessage')}
      </p>

      <div className="flex gap-3 justify-center">
        {showHomeLink && (
          <Button variant="outline" onClick={() => window.location.href = '/'}>
            {t('errors.goHome')}
          </Button>
        )}
        <Button className="vess-btn-primary gap-2" onClick={() => window.location.reload()}>
          <RefreshCw className="h-4 w-4" />
          {t('errors.tryAgain')}
        </Button>
      </div>
    </div>
  );
}

export function SectionErrorFallback(props: FallbackProps) {
  return <ErrorFallback {...props} showHomeLink={false} />;
}
